import { Button, Space, Tag } from 'antd';
import { ShowButton } from '@refinedev/antd';
import { ColumnType } from '@/types';
import { EyeOutlined, UserAddOutlined } from '@ant-design/icons';
import { GetReq } from '@/api/types/getReq';
import { canAssign } from '@/utils/requestPermissions';
import { formatAmmanDateTime } from '@/utils/formatAmmanDateTime';

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'APPROVED':
      return 'green';
    case 'REJECTED':
      return 'red';
    case 'ASSIGNED':
      return 'blue';
    default:
      return 'orange';
  }
};

export const getColumns = (
  translate: (key: string) => string,
  push: (path: string) => void,
  openModal: (show: boolean, user: GetReq | undefined) => void,
  adminType?: string,
  onAssign?: (request: GetReq) => void
): ColumnType[] => [
  {
    title: translate('listing-request.id'),
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: translate('listing-request.user.title'),
    dataIndex: 'user_name',
    key: 'user_name',
  },
  {
    title: translate('listing-request.user.email'),
    dataIndex: 'user_email',
    key: 'user_email',
  },
  {
    title: translate('listing-request.postTitle.title'),
    dataIndex: 'post_title',
    key: 'post_title',
    render: (postTitle: string | null) => postTitle ?? '—',
  },
  {
    title: translate('listing-request.type.title'),
    dataIndex: 'type',
    key: 'type',
    render: (type: string) => translate(`listing-request.type.${type}`),
  },
  {
    title: translate('listing-request.status.title'),
    dataIndex: 'status',
    key: 'status',
    render: (status: string) => (
      <Tag color={getStatusColor(status)}>
        {translate(`listing-request.status.${status}`)}
      </Tag>
    ),
  },
  {
    title: translate('listing-request.stage.title'),
    dataIndex: 'stage',
    key: 'stage',
    render: (stage: string) => (
      <Tag color="blue">{translate(`listing-request.stage.${stage}`)}</Tag>
    ),
  },
  {
    title: translate('listing-request.assignedAdmin.title'),
    dataIndex: 'assigned_admin',
    key: 'assigned_admin',
    render: (_: unknown, record: GetReq) => record.assigned_admin?.name ?? '—',
  },
  {
    title: translate('listing-request.assignedAt.title'),
    dataIndex: 'assigned_at',
    key: 'assigned_at',
    render: (assignedAt: string | null) => formatAmmanDateTime(assignedAt),
  },
  {
    title: translate('listing-request.actions'),
    dataIndex: 'actions',
    key: 'actions',
    render: (_, record) => (
      <Space>
        {record.type === 'POST_SUBMISSION' ? (
          <>
            <ShowButton
              hideText
              size="small"
              recordItemId={record.post_id}
              onClick={() => {
                push(
                  `/listing-request/showPostReq/${record.post_id}/${record.id}`
                );
              }}
            />
            {canAssign(record, adminType) && onAssign && (
              <Button
                size="small"
                icon={<UserAddOutlined />}
                title={translate('listing-request.assign.button')}
                onClick={() => onAssign(record)}
              />
            )}
          </>
        ) : (
          <>
            {record.status === 'PENDING' && (
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => openModal(true, record)}
              />
            )}
          </>
        )}
      </Space>
    ),
  },
];
